import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Paginated } from '@/shared/api/client';
import type { ExtraService } from '@/shared/api/types';

export interface ExtraServiceFilters {
  q?: string;
  confirmedInternal?: boolean | '';
}

export function useExtraServices(filters: ExtraServiceFilters) {
  const params = new URLSearchParams({ limit: '100' });
  if (filters.q) params.set('q', filters.q);
  if (filters.confirmedInternal !== '' && filters.confirmedInternal !== undefined)
    params.set('confirmedInternal', String(filters.confirmedInternal));
  return useQuery({
    queryKey: ['extra-services', filters],
    queryFn: () => api<Paginated<ExtraService>>(`/api/extra-services?${params.toString()}`),
  });
}

export interface ExtraServiceDetail extends ExtraService {
  fieldReport: {
    id: string;
    noteText?: string | null;
    transcript?: string | null;
    status: string;
    submittedAt?: string | null;
    employee?: { displayName: string } | null;
    workTimes: { durationMinutes: number; note?: string | null }[];
    materials: { name: string; quantity: string; unit: string }[];
    project: {
      id: string;
      title: string;
      reference?: string | null;
      scopeOriginal?: string | null;
      customer: { companyName: string };
      object: { name: string; address?: string | null };
    };
  };
  approvalItems: {
    id: string;
    decision: string;
    approval: { decision: string; decidedAt?: string | null; sentAt?: string | null };
  }[];
}

export function useExtraService(id: string | null) {
  return useQuery({
    queryKey: ['extra-service', id],
    queryFn: () => api<ExtraServiceDetail>(`/api/extra-services/${id}`),
    enabled: !!id,
  });
}

export interface ConfirmExtraServiceInput {
  title?: string;
  description?: string;
  reason?: string;
  quantity?: number;
  unit?: string;
  netAmount: number;
}

export function useConfirmExtraService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ConfirmExtraServiceInput }) =>
      api<ExtraService>(`/api/extra-services/${id}/confirm`, { method: 'PATCH', body: input }),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ['extra-services'] });
      qc.invalidateQueries({ queryKey: ['extra-service', id] });
    },
  });
}

export function useRevokeExtraService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<ExtraService>(`/api/extra-services/${id}/revoke`, { method: 'PATCH' }),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['extra-services'] });
      qc.invalidateQueries({ queryKey: ['extra-service', id] });
    },
  });
}
